import { Avatar, AvatarFallback, AvatarImage } from '@radix-ui/react-avatar';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useRef } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { AttributeCard } from '@/components/Card/AttributeCard';
import Icon from '@/components/Icon/Icon';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { ProductLogoSmallLicenses } from '@/components/Logo';
import { Text } from '@/components/Typography/Text';
import { Confetti, type ConfettiRef } from '@/components/ui/confetti';
import { useAuthStore } from '@/lib/auth';
import { defaultNS, useTranslation } from '@/lib/i18n';

const CompletedSchema = z.object({
  addedLicenseID: z.string(),
  licenseName: z.string(),
});

export const Route = createFileRoute(
  '/_staff/licenses/add/$allocationType/completed',
)({
  validateSearch: zodValidator(CompletedSchema),
  loaderDeps: ({ search }) => ({ search }),
  component: () => <Completed />,
});

function Completed() {
  const confettiRef = useRef<ConfettiRef>(null);
  const { session } = useAuthStore();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.licenses.add.form.navigation.completed',
  });
  const { search } = Route.useLoaderDeps();
  const navigate = useNavigate();

  return (
    <VStack className="flex justify-center items-center">
      <Confetti ref={confettiRef} />
      <AttributeCard
        title={t('result')}
        className="p-4 w-2/4"
        actionButton={
          <ActionButton
            variant="secondary"
            onClick={() => {
              navigate({
                to: `/licenses/$id`,
                params: { id: search.addedLicenseID },
              });
            }}
          >
            {t('goToLicense')}
          </ActionButton>
        }
      >
        <VStack>
          <HStack className="items-center">
            <Text variant="h3">{t('message')}</Text>
            <Spacer />
            <ProductLogoSmallLicenses />
            <Spacer />
            <Text className="text-xl">{search.licenseName}</Text>
          </HStack>
          <Spacer />
          <HStack className="items-center">
            <Icon iconName="SquareCheckBig" text={t('completedBy')} />
            <Spacer />
            <Avatar className="grid place-items-center size-8">
              <AvatarImage src={session?.person.avatarUrl} />
              <AvatarFallback>
                <Icon iconName="UserRoundIcon" />
              </AvatarFallback>
            </Avatar>
            <Spacer />
            {session?.person?.name}
          </HStack>
        </VStack>
      </AttributeCard>
      <Spacer variant="xl" />
      <ActionButton
        variant="ghost"
        onClick={() => {
          navigate({ to: '/licenses/add' });
        }}
        icon="PlusIcon"
        iconColor="active"
      >
        {t('continueAdding')}
      </ActionButton>
    </VStack>
  );
}
